import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Card,
  Button,
  Typography,
  Space,
  Spin,
  Tag,
  Tabs,
  Form,
  Input,
  Select,
  DatePicker,
  Table,
  Switch,
  InputNumber,
  message,
  Alert,
  Upload,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowLeftOutlined,
  SaveOutlined,
  SendOutlined,
  CheckCircleOutlined,
  PlayCircleOutlined,
  AuditOutlined,
  LockOutlined,
  UploadOutlined,
  FileOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import RequestTrainingButton from '@/components/competency/RequestTrainingButton';
import dayjs from 'dayjs';
import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import type { User } from '@/types';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';
import MocHandoverSection from '@/components/moc/MocHandoverSection';
import {
  mocService,
  MOC_CHANGE_TYPE_KEY_PERSONNEL,
  MOC_IMPACT_DIMENSIONS,
  MocHeader,
  MocImpactRow,
  MocRiskRow,
  MocNotificationRow,
  MocImplementationRow,
  MocChangeType,
  MocAttachmentRow,
} from '../../services/moc.service';
import { documentService } from '../../services/document.service';
import { ApprovalPanel } from '@/components/approval/ApprovalPanel';
import { API_BASE_URL } from '@/config/apiBaseUrl';

const { Title, Text } = Typography;
const { TextArea } = Input;

const statusColors: Record<string, string> = {
  draft: 'default',
  submitted: 'processing',
  under_review: 'processing',
  risk_assessment_pending: 'orange',
  approval_pending: 'gold',
  approved_for_implementation: 'cyan',
  implementation_in_progress: 'blue',
  verification_pending: 'purple',
  closed: 'success',
  rejected: 'error',
  cancelled: 'default',
};

const TARGET_KINDS = [
  { value: 'role', label: 'Role' },
  { value: 'user', label: 'User' },
  { value: 'department', label: 'Department' },
  { value: 'customer', label: 'Customer' },
  { value: 'supplier', label: 'Supplier' },
  { value: 'process_owner', label: 'Process owner' },
];

const ENTITY_TYPE_MOC = 'management_of_change';

const ATTACHMENT_KINDS = [
  { value: 'evidence', label: 'Evidence' },
  { value: 'risk', label: 'Risk / assessment' },
  { value: 'approval', label: 'Approval' },
  { value: 'other', label: 'Other' },
];

const UUID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function canUse(user: User | null, permission: string): boolean {
  if (!user) return false;
  if (user.is_super_admin) return true;
  return Boolean(user.permissions?.includes(permission));
}

const MOCDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  const [row, setRow] = useState<MocHeader | null>(null);
  const [types, setTypes] = useState<MocChangeType[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wfLoading, setWfLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [verificationDirty, setVerificationDirty] = useState(false);
  const verificationHydratingRef = useRef(false);
  const [attachments, setAttachments] = useState<MocAttachmentRow[]>([]);
  const [attachmentKind, setAttachmentKind] = useState<string | undefined>(undefined);
  const [attachmentTitle, setAttachmentTitle] = useState('');
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [uploadingCommunicationDoc, setUploadingCommunicationDoc] = useState(false);

  const [overviewForm] = Form.useForm();
  const [verificationForm] = Form.useForm();

  const relatedWorkOrderId = Form.useWatch('related_work_order_id', overviewForm);
  const relatedProjectId = Form.useWatch('related_project_id', overviewForm);
  const relatedSupplierId = Form.useWatch('related_supplier_id', overviewForm);
  const relatedSalesOrderId = Form.useWatch('related_sales_order_id', overviewForm);
  const relatedAssetId = Form.useWatch('related_asset_id', overviewForm);
  const custNotifyRequired = Form.useWatch('customer_notification_required', overviewForm);
  const commDocId = Form.useWatch('customer_communication_document_id', overviewForm);

  const [impacts, setImpacts] = useState<MocImpactRow[]>([]);
  const [riskRows, setRiskRows] = useState<MocRiskRow[]>([]);
  const [notifications, setNotifications] = useState<MocNotificationRow[]>([]);
  const [implActions, setImplActions] = useState<MocImplementationRow[]>([]);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [data, ct] = await Promise.all([mocService.getById(id), mocService.getChangeTypes()]);
      setRow(data);
      setTypes(ct);
      overviewForm.setFieldsValue({
        moc_date: data.moc_date ? dayjs(data.moc_date) : undefined,
        change_type_id: data.change_type_id,
        change_title: data.change_title,
        change_category: data.change_category,
        department_id: data.department_id || undefined,
        department: data.department,
        business_unit_site: data.business_unit_site,
        related_process_area: data.related_process_area,
        related_work_order_id: data.related_work_order_id,
        related_project_id: data.related_project_id,
        related_supplier_id: data.related_supplier_id,
        related_sales_order_id: data.related_sales_order_id,
        related_asset_id: data.related_asset_id,
        procedure_reference: data.procedure_reference,
        urgency: data.urgency,
        reason_for_change: data.reason_for_change,
        detailed_description: data.detailed_description,
        business_justification: data.business_justification,
        effective_date_requested: data.effective_date_requested ? dayjs(data.effective_date_requested) : undefined,
        customer_notification_required: data.customer_notification_required,
        customer_notification_reason: data.customer_notification_reason,
        customer_notification_method: data.customer_notification_method,
        customer_notification_sent_at: data.customer_notification_sent_at
          ? dayjs(data.customer_notification_sent_at)
          : undefined,
        customer_notification_ack_notes: data.customer_notification_ack_notes,
        customer_communication_document_id: data.customer_communication_document_id,
        manpower_impact_notes: data.manpower_impact_notes,
        training_required_notes: data.training_required_notes,
        tooling_equipment_notes: data.tooling_equipment_notes,
        supplier_impact_notes: data.supplier_impact_notes,
        documentation_updates_notes: data.documentation_updates_notes,
        qualification_validation_notes: data.qualification_validation_notes,
        implementation_prerequisites: data.implementation_prerequisites,
        resource_readiness_comments: data.resource_readiness_comments,
        implementation_plan: data.implementation_plan,
        implementation_dependencies: data.implementation_dependencies,
      });
      setImpacts(data.impacts ?? []);
      setRiskRows(data.risk_rows ?? []);
      setNotifications(data.notification_matrix ?? []);
      setImplActions(data.implementation_actions ?? []);
      setAttachments(data.attachments ?? []);
      const v = data.verification;
      verificationHydratingRef.current = true;
      verificationForm.setFieldsValue({
        implementation_completed: v?.implementation_completed ?? false,
        verification_performed: v?.verification_performed ?? false,
        effectiveness_confirmed: v?.effectiveness_confirmed ?? false,
        adverse_impact_observed: v?.adverse_impact_observed ?? false,
        additional_action_required: v?.additional_action_required ?? false,
        verification_evidence_notes: v?.verification_evidence_notes,
        verification_date: v?.verification_date ? dayjs(v.verification_date) : undefined,
      });
      setTimeout(() => {
        verificationHydratingRef.current = false;
        setVerificationDirty(false);
      }, 0);
    } catch {
      message.error('Failed to load MOC');
      navigate('/grc/moc');
    } finally {
      setLoading(false);
    }
  }, [id, navigate, overviewForm, verificationForm]);

  useEffect(() => {
    void load();
  }, [load]);

  const status = row?.status ?? '';
  const blockedEdit = ['closed', 'rejected', 'cancelled', 'approval_pending'].includes(status);

  const savePayload = useMemo(() => {
    return async () => {
      if (!id || !row) return;
      const o = await overviewForm.validateFields();
      const verVals = verificationForm.getFieldsValue();
      const payload: Record<string, unknown> = {
        change_type_id: o.change_type_id,
        change_title: o.change_title,
        change_category: o.change_category,
        department_id: o.department_id,
        business_unit_site: o.business_unit_site,
        related_process_area: o.related_process_area,
        related_work_order_id: (o.related_work_order_id || '').trim() || undefined,
        related_project_id: (o.related_project_id || '').trim() || undefined,
        related_supplier_id: (o.related_supplier_id || '').trim() || undefined,
        related_sales_order_id: (o.related_sales_order_id || '').trim() || undefined,
        related_asset_id: (o.related_asset_id || '').trim() || undefined,
        procedure_reference: o.procedure_reference,
        urgency: o.urgency,
        reason_for_change: o.reason_for_change,
        detailed_description: o.detailed_description,
        business_justification: o.business_justification,
        moc_date: o.moc_date ? (o.moc_date as dayjs.Dayjs).format('YYYY-MM-DD') : undefined,
        effective_date_requested: o.effective_date_requested
          ? (o.effective_date_requested as dayjs.Dayjs).format('YYYY-MM-DD')
          : undefined,
        customer_notification_required: o.customer_notification_required,
        customer_notification_reason: o.customer_notification_reason,
        customer_notification_method: o.customer_notification_method,
        customer_notification_sent_at: o.customer_notification_sent_at
          ? (o.customer_notification_sent_at as dayjs.Dayjs).toISOString()
          : undefined,
        customer_notification_ack_notes: o.customer_notification_ack_notes,
        customer_communication_document_id: (o.customer_communication_document_id || '').trim() || undefined,
        manpower_impact_notes: o.manpower_impact_notes,
        training_required_notes: o.training_required_notes,
        tooling_equipment_notes: o.tooling_equipment_notes,
        supplier_impact_notes: o.supplier_impact_notes,
        documentation_updates_notes: o.documentation_updates_notes,
        qualification_validation_notes: o.qualification_validation_notes,
        implementation_prerequisites: o.implementation_prerequisites,
        resource_readiness_comments: o.resource_readiness_comments,
        implementation_plan: o.implementation_plan,
        implementation_dependencies: o.implementation_dependencies,
        impacts: impacts.filter((i) => i.dimension_code?.trim()),
        risk_rows: riskRows.filter((r) => (r.hazard_failure_mode || '').trim()),
        notification_matrix: notifications.filter((n) => n.target_kind?.trim()),
        implementation_actions: implActions.filter((a) => (a.action_description || '').trim()),
        verification: {
          implementation_completed: verVals.implementation_completed,
          verification_performed: verVals.verification_performed,
          effectiveness_confirmed: verVals.effectiveness_confirmed,
          adverse_impact_observed: verVals.adverse_impact_observed,
          additional_action_required: verVals.additional_action_required,
          verification_evidence_notes: verVals.verification_evidence_notes,
          verification_date: verVals.verification_date
            ? (verVals.verification_date as dayjs.Dayjs).format('YYYY-MM-DD')
            : undefined,
        },
        attachments: attachments.map((a) => ({
          id: a.id,
          attachment_kind: a.attachment_kind,
          title: a.title,
          file_path: a.file_path,
          document_id: a.document_id,
        })),
      };
      return payload;
    };
  }, [
    id,
    row,
    overviewForm,
    verificationForm,
    impacts,
    riskRows,
    notifications,
    implActions,
    attachments,
  ]);

  const onSave = async () => {
    if (!id || blockedEdit) return;
    setSaving(true);
    try {
      const payload = await savePayload();
      if (!payload) return;
      const updated = await mocService.update(id, payload);
      setRow(updated);
      message.success('Saved');
      await load();
    } catch (e: unknown) {
      if ((e as { errorFields?: unknown })?.errorFields) return;
      console.error(e);
      message.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const runWf = async (fn: () => Promise<MocHeader>, okMsg: string) => {
    setWfLoading(true);
    try {
      await fn();
      message.success(okMsg);
      if (id) {
        await queryClient.invalidateQueries({ queryKey: ['approval-summary', ENTITY_TYPE_MOC, id] });
      }
      await load();
    } catch (e: unknown) {
      console.error(e);
      const apiMsg = axios.isAxiosError(e)
        ? (e.response?.data as { message?: string } | undefined)?.message
        : undefined;
      message.error(apiMsg || 'Action failed');
    } finally {
      setWfLoading(false);
    }
  };

  const downloadMocDocument = async (documentId: string, fileName: string) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(String(response.status));
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName || 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: unknown) {
      console.error(e);
      message.error('Download failed');
    }
  };

  const handleAttachmentUpload = async (file: File) => {
    if (!id || blockedEdit) return;
    setUploadingAttachment(true);
    try {
      const doc = await documentService.uploadDocument(file, {
        document_name: file.name,
        entity_type: ENTITY_TYPE_MOC,
        entity_id: id,
      });
      const next: MocAttachmentRow[] = [
        ...attachments,
        {
          attachment_kind: attachmentKind || undefined,
          title: (attachmentTitle || '').trim() || doc.document_name || file.name,
          document_id: doc.id,
          file_path: doc.file_path,
        },
      ];
      const updated = await mocService.update(id, { attachments: next });
      setAttachments(updated.attachments ?? next);
      setRow(updated);
      message.success('Attachment added');
    } catch (e: unknown) {
      console.error(e);
      message.error('Upload failed');
    } finally {
      setUploadingAttachment(false);
    }
  };

  const removeAttachmentAt = async (index: number) => {
    if (!id || blockedEdit) return;
    const next = attachments.filter((_, i) => i !== index);
    setSaving(true);
    try {
      const updated = await mocService.update(id, { attachments: next });
      setAttachments(updated.attachments ?? next);
      setRow(updated);
      message.success('Attachment removed');
    } catch (e: unknown) {
      console.error(e);
      message.error('Remove failed');
    } finally {
      setSaving(false);
    }
  };

  const handleCommunicationDocUpload = async (file: File) => {
    if (!id || blockedEdit) return;
    setUploadingCommunicationDoc(true);
    try {
      const doc = await documentService.uploadDocument(file, {
        document_name: file.name,
        entity_type: ENTITY_TYPE_MOC,
        entity_id: id,
      });
      overviewForm.setFieldsValue({ customer_communication_document_id: doc.id });
      const updated = await mocService.update(id, { customer_communication_document_id: doc.id });
      setRow(updated);
      message.success('Communication document linked');
    } catch (e: unknown) {
      console.error(e);
      message.error('Upload failed');
    } finally {
      setUploadingCommunicationDoc(false);
    }
  };

  const handleCloseMoc = async () => {
    if (!row?.id || !id) return;
    try {
      await verificationForm.validateFields();
      if (verificationDirty) {
        setSaving(true);
        try {
          const payload = await savePayload();
          if (!payload) return;
          await mocService.update(id, payload);
          setVerificationDirty(false);
          message.success('Saved');
          await load();
        } catch (e: unknown) {
          if ((e as { errorFields?: unknown })?.errorFields) return;
          console.error(e);
          message.error('Save failed');
          return;
        } finally {
          setSaving(false);
        }
      }
      await runWf(() => mocService.close(row.id), 'MOC closed');
    } catch (e: unknown) {
      if ((e as { errorFields?: unknown })?.errorFields) {
        message.warning('Complete verification fields before closing.');
        setActiveTab('verification');
        return;
      }
      console.error(e);
    }
  };

  const impactColumns: ColumnsType<MocImpactRow & { _key: number }> = [
    {
      title: 'Dimension',
      render: (_, __, index) => (
        <Select
          style={{ minWidth: 200 }}
          disabled={blockedEdit}
          value={impacts[index]?.dimension_code}
          options={MOC_IMPACT_DIMENSIONS.map((d) => ({ value: d.code, label: d.label }))}
          onChange={(v) => {
            const next = [...impacts];
            next[index] = { ...next[index], dimension_code: v };
            setImpacts(next);
          }}
        />
      ),
    },
    {
      title: 'Level',
      width: 120,
      render: (_, __, index) => (
        <Select
          disabled={blockedEdit}
          value={impacts[index]?.impact_level || 'low'}
          options={[
            { value: 'low', label: 'Low' },
            { value: 'medium', label: 'Medium' },
            { value: 'high', label: 'High' },
          ]}
          onChange={(v) => {
            const next = [...impacts];
            next[index] = { ...next[index], impact_level: v };
            setImpacts(next);
          }}
        />
      ),
    },
    {
      title: 'Comments',
      render: (_, __, index) => (
        <Input
          disabled={blockedEdit}
          value={impacts[index]?.comments ?? ''}
          onChange={(e) => {
            const next = [...impacts];
            next[index] = { ...next[index], comments: e.target.value };
            setImpacts(next);
          }}
        />
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, __, index) => (
        <Button
          type="link"
          danger
          disabled={blockedEdit}
          onClick={() => setImpacts(impacts.filter((_, i) => i !== index))}
        >
          Remove
        </Button>
      ),
    },
  ];

  const riskColumns: ColumnsType<MocRiskRow & { _key: number }> = [
    {
      title: 'Hazard / failure mode',
      render: (_, __, index) => (
        <Input
          disabled={blockedEdit}
          value={riskRows[index]?.hazard_failure_mode ?? ''}
          onChange={(e) => {
            const next = [...riskRows];
            next[index] = { ...next[index], hazard_failure_mode: e.target.value };
            setRiskRows(next);
          }}
        />
      ),
    },
    {
      title: 'Sev',
      width: 72,
      render: (_, __, index) => (
        <InputNumber
          min={1}
          max={10}
          disabled={blockedEdit}
          value={riskRows[index]?.severity ?? undefined}
          onChange={(v) => {
            const next = [...riskRows];
            next[index] = { ...next[index], severity: v ?? undefined };
            setRiskRows(next);
          }}
        />
      ),
    },
    {
      title: 'L',
      width: 72,
      render: (_, __, index) => (
        <InputNumber
          min={1}
          max={10}
          disabled={blockedEdit}
          value={riskRows[index]?.likelihood ?? undefined}
          onChange={(v) => {
            const next = [...riskRows];
            next[index] = { ...next[index], likelihood: v ?? undefined };
            setRiskRows(next);
          }}
        />
      ),
    },
    {
      title: 'Mitigation',
      render: (_, __, index) => (
        <Input
          disabled={blockedEdit}
          value={riskRows[index]?.mitigation_controls ?? ''}
          onChange={(e) => {
            const next = [...riskRows];
            next[index] = { ...next[index], mitigation_controls: e.target.value };
            setRiskRows(next);
          }}
        />
      ),
    },
    {
      title: '',
      width: 80,
      render: (_, __, index) => (
        <Button
          type="link"
          danger
          disabled={blockedEdit}
          onClick={() => setRiskRows(riskRows.filter((_, i) => i !== index))}
        >
          Remove
        </Button>
      ),
    },
  ];

  const histSorted = useMemo(() => {
    const h = row?.status_history ?? [];
    return [...h].sort((a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf());
  }, [row?.status_history]);

  if (loading || !row) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  const perm = {
    submit: canUse(user, 'moc.submit'),
    approve: canUse(user, 'moc.approve'),
    verify: canUse(user, 'moc.verify'),
    edit: canUse(user, 'moc.edit'),
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/grc/moc')}>
            Back to register
          </Button>
          <Space align="center" wrap style={{ marginTop: 8 }}>
            <Title level={4} style={{ margin: 0 }}>
              {row.moc_number}
            </Title>
            <Tag color={statusColors[row.status] || 'default'}>{row.status.replace(/_/g, ' ')}</Tag>
            {row.risk_assessment_complete ? <Tag color="green">Risk documented</Tag> : null}
          </Space>
          <Text type="secondary">{row.change_title}</Text>
        </div>

        {blockedEdit && (
          <Alert
            type={status === 'approval_pending' ? 'info' : 'warning'}
            showIcon
            icon={status === 'approval_pending' ? <LockOutlined /> : undefined}
            message={
              status === 'approval_pending'
                ? 'This MOC is in approval. Edits are locked until the workflow completes.'
                : 'This record is closed or read-only in its current state.'
            }
          />
        )}

        <Card size="small">
          <Space wrap>
            <Button
              type="primary"
              icon={<SaveOutlined />}
              loading={saving}
              disabled={blockedEdit || !perm.edit}
              onClick={() => void onSave()}
            >
              Save changes
            </Button>
            <RequestTrainingButton sourceType="MOC" sourceId={row.id} defaultNotes={`${row.moc_number}: ${row.change_title}`} />
            {status === 'draft' && perm.submit ? (
              <Button
                loading={wfLoading}
                icon={<SendOutlined />}
                onClick={() =>
                  runWf(() => mocService.submit(row.id), 'Submitted')
                }
              >
                Submit
              </Button>
            ) : null}
            {['submitted', 'under_review', 'risk_assessment_pending'].includes(status) && perm.submit ? (
              <Button
                type="primary"
                loading={wfLoading}
                icon={<CheckCircleOutlined />}
                onClick={() => runWf(() => mocService.submitForApproval(row.id), 'Sent for approval')}
              >
                Submit for approval
              </Button>
            ) : null}
            {status === 'approval_pending' ? (
              <Text type="secondary">
                Awaiting universal approval. Status moves to &quot;Approved for implementation&quot; when the workflow
                completes.
              </Text>
            ) : null}
            {status === 'approved_for_implementation' && perm.approve ? (
              <Button
                loading={wfLoading}
                icon={<PlayCircleOutlined />}
                onClick={() => runWf(() => mocService.startImplementation(row.id), 'Implementation started')}
              >
                Start implementation
              </Button>
            ) : null}
            {status === 'implementation_in_progress' && perm.verify ? (
              <Button
                loading={wfLoading}
                icon={<AuditOutlined />}
                onClick={() => runWf(() => mocService.requestVerification(row.id), 'Verification requested')}
              >
                Request verification
              </Button>
            ) : null}
            {status === 'verification_pending' && perm.verify ? (
              <Button type="primary" loading={wfLoading || saving} onClick={() => void handleCloseMoc()}>
                Close MOC
              </Button>
            ) : null}
          </Space>
        </Card>

        <Card size="small" title="Approvals">
          <Space direction="vertical" style={{ width: '100%' }}>
            <Link to="/home/approvals">Open approval inbox</Link>
            {row?.id ? (
              <ApprovalPanel entityType={ENTITY_TYPE_MOC} entityId={row.id} extraInvalidateQueryKeys={[['moc', row.id]]} />
            ) : null}
          </Space>
        </Card>

        {row.changeType?.code === MOC_CHANGE_TYPE_KEY_PERSONNEL ? (
          <MocHandoverSection
            key={`${row.id}:${status}`}
            mocId={row.id}
            mocStatus={status}
            canEdit={perm.edit}
            canExecute={perm.approve}
          />
        ) : null}

        <Tabs
          activeKey={activeTab}
          onChange={(k) => setActiveTab(k)}
          items={[
            {
              key: 'overview',
              label: 'Overview',
              children: (
                <Form form={overviewForm} layout="vertical" disabled={blockedEdit}>
                  <Form.Item name="moc_date" label="MOC date" rules={[{ required: true }]}>
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="change_type_id" label="Change type" rules={[{ required: true }]}>
                    <Select
                      showSearch
                      optionFilterProp="label"
                      options={types.map((t) => ({ value: t.id, label: `${t.name} (${t.code})` }))}
                    />
                  </Form.Item>
                  <Form.Item name="change_title" label="Change title" rules={[{ required: true }]}>
                    <Input maxLength={500} showCount />
                  </Form.Item>
                  <Form.Item name="change_category" label="Category">
                    <Input />
                  </Form.Item>
                  <Form.Item name="urgency" label="Urgency">
                    <Select
                      options={[
                        { value: 'routine', label: 'Routine' },
                        { value: 'urgent', label: 'Urgent' },
                        { value: 'emergency', label: 'Emergency' },
                      ]}
                    />
                  </Form.Item>
                  <Form.Item
                    name="department_id"
                    label="Department"
                    rules={[{ required: true, message: 'Department is required' }]}
                  >
                    <DepartmentSelect />
                  </Form.Item>
                  <Form.Item name="business_unit_site" label="Business unit / site">
                    <Input />
                  </Form.Item>
                  <Form.Item name="related_process_area" label="Related process / area">
                    <Input />
                  </Form.Item>
                  <Form.Item name="procedure_reference" label="Procedure reference">
                    <Input />
                  </Form.Item>
                  <Title level={5}>ERP links (optional)</Title>
                  <Text type="secondary">Paste record UUIDs to deep-link related documents.</Text>
                  <Form.Item
                    name="related_work_order_id"
                    label="Related work order"
                    extra={
                      relatedWorkOrderId && UUID_RE.test(String(relatedWorkOrderId)) ? (
                        <Link to={`/mfg/work-orders/${relatedWorkOrderId}`} target="_blank" rel="noopener noreferrer">
                          Open work order
                        </Link>
                      ) : null
                    }
                  >
                    <Input placeholder="Work order UUID" />
                  </Form.Item>
                  <Form.Item
                    name="related_project_id"
                    label="Related project"
                    extra={
                      relatedProjectId && UUID_RE.test(String(relatedProjectId)) ? (
                        <Link to={`/prj/projects/${relatedProjectId}/edit`} target="_blank" rel="noopener noreferrer">
                          Open project
                        </Link>
                      ) : null
                    }
                  >
                    <Input placeholder="Project UUID" />
                  </Form.Item>
                  <Form.Item
                    name="related_supplier_id"
                    label="Related supplier"
                    extra={
                      relatedSupplierId && UUID_RE.test(String(relatedSupplierId)) ? (
                        <Link
                          to={`/scm/procurement/suppliers/${relatedSupplierId}/edit`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Open supplier
                        </Link>
                      ) : null
                    }
                  >
                    <Input placeholder="Supplier UUID" />
                  </Form.Item>
                  <Form.Item
                    name="related_sales_order_id"
                    label="Related customer order"
                    extra={
                      relatedSalesOrderId && UUID_RE.test(String(relatedSalesOrderId)) ? (
                        <Link to={`/growth/orders/${relatedSalesOrderId}`} target="_blank" rel="noopener noreferrer">
                          Open customer order
                        </Link>
                      ) : null
                    }
                  >
                    <Input placeholder="Customer order UUID" />
                  </Form.Item>
                  <Form.Item
                    name="related_asset_id"
                    label="Related asset"
                    extra={
                      relatedAssetId && UUID_RE.test(String(relatedAssetId)) ? (
                        <Link to={`/asset/assets/${relatedAssetId}/edit`} target="_blank" rel="noopener noreferrer">
                          Open asset
                        </Link>
                      ) : null
                    }
                  >
                    <Input placeholder="Asset UUID" />
                  </Form.Item>
                  <Form.Item name="reason_for_change" label="Reason for change" rules={[{ required: true }]}>
                    <TextArea rows={4} />
                  </Form.Item>
                  <Form.Item name="detailed_description" label="Detailed description" rules={[{ required: true }]}>
                    <TextArea rows={6} />
                  </Form.Item>
                  <Form.Item name="business_justification" label="Business justification">
                    <TextArea rows={3} />
                  </Form.Item>
                  <Form.Item name="effective_date_requested" label="Effective date requested">
                    <DatePicker style={{ width: '100%' }} />
                  </Form.Item>
                  <Form.Item name="customer_notification_required" label="Customer notification required" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                  <Form.Item name="customer_notification_reason" label="Customer notification reason">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="customer_notification_method" label="Customer notification method">
                    <Input placeholder="e.g. email, formal letter" />
                  </Form.Item>
                  {custNotifyRequired ? (
                    <>
                      <Title level={5}>Customer notification lifecycle</Title>
                      <Form.Item name="customer_notification_sent_at" label="Date / time notified">
                        <DatePicker showTime style={{ width: '100%' }} />
                      </Form.Item>
                      <Form.Item name="customer_notification_ack_notes" label="Acknowledgement / response">
                        <TextArea rows={3} placeholder="Customer acknowledgement, response, or follow-up notes" />
                      </Form.Item>
                      <Form.Item label="Linked communication record">
                        <Space wrap align="center">
                          <Form.Item name="customer_communication_document_id" noStyle>
                            <Input placeholder="Document UUID (paste or upload)" style={{ minWidth: 280 }} />
                          </Form.Item>
                          <Upload
                            beforeUpload={(file) => {
                              void handleCommunicationDocUpload(file);
                              return false;
                            }}
                            showUploadList={false}
                            disabled={blockedEdit}
                          >
                            <Button loading={uploadingCommunicationDoc} disabled={blockedEdit}>
                              Upload &amp; link
                            </Button>
                          </Upload>
                          {commDocId ? (
                            <Button
                              type="link"
                              onClick={() => void downloadMocDocument(String(commDocId), 'customer-communication')}
                            >
                              Download
                            </Button>
                          ) : null}
                        </Space>
                      </Form.Item>
                    </>
                  ) : null}
                  <Title level={5}>Resources &amp; readiness</Title>
                  <Form.Item name="manpower_impact_notes" label="Manpower impact">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="training_required_notes" label="Training required">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="tooling_equipment_notes" label="Tooling / equipment">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="supplier_impact_notes" label="Supplier impact">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="documentation_updates_notes" label="Documentation updates">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="qualification_validation_notes" label="Qualification / validation">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="implementation_prerequisites" label="Implementation prerequisites">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="resource_readiness_comments" label="Readiness comments">
                    <TextArea rows={2} />
                  </Form.Item>
                  <Form.Item name="implementation_plan" label="Implementation plan">
                    <TextArea rows={4} />
                  </Form.Item>
                  <Form.Item name="implementation_dependencies" label="Dependencies">
                    <TextArea rows={2} />
                  </Form.Item>
                </Form>
              ),
            },
            {
              key: 'impacts',
              label: 'Impact assessment',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    disabled={blockedEdit}
                    onClick={() =>
                      setImpacts([
                        ...impacts,
                        { dimension_code: 'product_quality', impact_level: 'low', comments: '' },
                      ])
                    }
                  >
                    Add impact row
                  </Button>
                  <Table
                    rowKey={(_, i) => String(i)}
                    pagination={false}
                    columns={impactColumns}
                    dataSource={impacts.map((r, i) => ({ ...r, _key: i }))}
                  />
                </Space>
              ),
            },
            {
              key: 'risk',
              label: 'Risk assessment',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    disabled={blockedEdit}
                    onClick={() =>
                      setRiskRows([
                        ...riskRows,
                        {
                          hazard_failure_mode: '',
                          severity: undefined,
                          likelihood: undefined,
                          mitigation_controls: '',
                        },
                      ])
                    }
                  >
                    Add risk row
                  </Button>
                  <Table
                    rowKey={(_, i) => String(i)}
                    pagination={false}
                    columns={riskColumns}
                    dataSource={riskRows.map((r, i) => ({ ...r, _key: i }))}
                  />
                </Space>
              ),
            },
            {
              key: 'notifications',
              label: 'Notification matrix',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    disabled={blockedEdit}
                    onClick={() =>
                      setNotifications([
                        ...notifications,
                        { target_kind: 'role', target_label: '', is_mandatory: true },
                      ])
                    }
                  >
                    Add notification row
                  </Button>
                  <Table
                    rowKey={(_, i) => String(i)}
                    pagination={false}
                    columns={[
                      {
                        title: 'Target kind',
                        render: (_, __, index) => (
                          <Select
                            style={{ width: 140 }}
                            disabled={blockedEdit}
                            value={notifications[index]?.target_kind}
                            options={TARGET_KINDS}
                            onChange={(v) => {
                              const next = [...notifications];
                              next[index] = { ...next[index], target_kind: v };
                              setNotifications(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: 'Label / note',
                        render: (_, __, index) => (
                          <Input
                            disabled={blockedEdit}
                            value={notifications[index]?.target_label ?? ''}
                            onChange={(e) => {
                              const next = [...notifications];
                              next[index] = { ...next[index], target_label: e.target.value };
                              setNotifications(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: 'Mandatory',
                        width: 100,
                        render: (_, __, index) => (
                          <Switch
                            disabled={blockedEdit}
                            checked={notifications[index]?.is_mandatory !== false}
                            onChange={(v) => {
                              const next = [...notifications];
                              next[index] = { ...next[index], is_mandatory: v };
                              setNotifications(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: '',
                        width: 80,
                        render: (_, __, index) => (
                          <Button
                            type="link"
                            danger
                            disabled={blockedEdit}
                            onClick={() => setNotifications(notifications.filter((_, i) => i !== index))}
                          >
                            Remove
                          </Button>
                        ),
                      },
                    ]}
                    dataSource={notifications.map((r, i) => ({ ...r, _key: i }))}
                  />
                </Space>
              ),
            },
            {
              key: 'implementation',
              label: 'Implementation actions',
              children: (
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    disabled={blockedEdit}
                    onClick={() =>
                      setImplActions([
                        ...implActions,
                        { action_description: '', completed: false },
                      ])
                    }
                  >
                    Add action
                  </Button>
                  <Table
                    rowKey={(_, i) => String(i)}
                    pagination={false}
                    columns={[
                      {
                        title: 'Action',
                        render: (_, __, index) => (
                          <Input
                            disabled={blockedEdit}
                            value={implActions[index]?.action_description ?? ''}
                            onChange={(e) => {
                              const next = [...implActions];
                              next[index] = { ...next[index], action_description: e.target.value };
                              setImplActions(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: 'Target date',
                        width: 160,
                        render: (_, __, index) => (
                          <DatePicker
                            disabled={blockedEdit}
                            value={
                              implActions[index]?.target_date
                                ? dayjs(implActions[index].target_date)
                                : undefined
                            }
                            onChange={(d) => {
                              const next = [...implActions];
                              next[index] = {
                                ...next[index],
                                target_date: d ? d.format('YYYY-MM-DD') : undefined,
                              };
                              setImplActions(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: 'Done',
                        width: 80,
                        render: (_, __, index) => (
                          <Switch
                            disabled={blockedEdit}
                            checked={implActions[index]?.completed ?? false}
                            onChange={(v) => {
                              const next = [...implActions];
                              next[index] = { ...next[index], completed: v };
                              setImplActions(next);
                            }}
                          />
                        ),
                      },
                      {
                        title: '',
                        width: 80,
                        render: (_, __, index) => (
                          <Button
                            type="link"
                            danger
                            disabled={blockedEdit}
                            onClick={() => setImplActions(implActions.filter((_, i) => i !== index))}
                          >
                            Remove
                          </Button>
                        ),
                      },
                    ]}
                    dataSource={implActions.map((r, i) => ({ ...r, _key: i }))}
                  />
                </Space>
              ),
            },
            {
              key: 'verification',
              label: 'Verification',
              children: (
                <>
                  {status === 'verification_pending' && verificationDirty ? (
                    <Alert
                      type="warning"
                      showIcon
                      style={{ marginBottom: 16 }}
                      message="Verification has unsaved changes"
                      description="Save the record or use Close MOC — your verification edits will be saved automatically when you close."
                    />
                  ) : null}
                  <Form
                    form={verificationForm}
                    layout="vertical"
                    disabled={blockedEdit}
                    onValuesChange={() => {
                      if (verificationHydratingRef.current) return;
                      setVerificationDirty(true);
                    }}
                  >
                    <Form.Item
                      name="implementation_completed"
                      valuePropName="checked"
                      label="Implementation completed"
                      rules={
                        status === 'verification_pending'
                          ? [
                              {
                                validator: (_, v) =>
                                  v ? Promise.resolve() : Promise.reject(new Error('Required before close')),
                              },
                            ]
                          : []
                      }
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="verification_performed"
                      valuePropName="checked"
                      label="Verification performed"
                      rules={
                        status === 'verification_pending'
                          ? [
                              {
                                validator: (_, v) =>
                                  v ? Promise.resolve() : Promise.reject(new Error('Required before close')),
                              },
                            ]
                          : []
                      }
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="effectiveness_confirmed"
                      valuePropName="checked"
                      label="Effectiveness confirmed"
                      rules={
                        status === 'verification_pending'
                          ? [
                              {
                                validator: (_, v) =>
                                  v ? Promise.resolve() : Promise.reject(new Error('Required before close')),
                              },
                            ]
                          : []
                      }
                    >
                      <Switch />
                    </Form.Item>
                    <Form.Item name="adverse_impact_observed" valuePropName="checked" label="Adverse impact observed">
                      <Switch />
                    </Form.Item>
                    <Form.Item name="additional_action_required" valuePropName="checked" label="Additional action required">
                      <Switch />
                    </Form.Item>
                    <Form.Item
                      name="verification_evidence_notes"
                      label="Evidence / notes"
                      rules={
                        status === 'verification_pending'
                          ? [{ required: true, message: 'Evidence / notes are required before close' }]
                          : []
                      }
                    >
                      <TextArea rows={4} />
                    </Form.Item>
                    <Form.Item
                      name="verification_date"
                      label="Verification date"
                      rules={
                        status === 'verification_pending'
                          ? [{ required: true, message: 'Verification date is required before close' }]
                          : []
                      }
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                  </Form>
                </>
              ),
            },
            {
              key: 'attachments',
              label: 'Attachments',
              children: (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Space wrap align="center">
                    <Select
                      style={{ minWidth: 160 }}
                      placeholder="Kind"
                      allowClear
                      disabled={blockedEdit}
                      options={ATTACHMENT_KINDS}
                      value={attachmentKind}
                      onChange={(v) => setAttachmentKind(v)}
                    />
                    <Input
                      style={{ minWidth: 200 }}
                      placeholder="Title (optional)"
                      disabled={blockedEdit}
                      value={attachmentTitle}
                      onChange={(e) => setAttachmentTitle(e.target.value)}
                    />
                    <Upload
                      beforeUpload={(file) => {
                        void handleAttachmentUpload(file);
                        return false;
                      }}
                      showUploadList={false}
                      disabled={blockedEdit}
                    >
                      <Button icon={<UploadOutlined />} loading={uploadingAttachment} disabled={blockedEdit}>
                        Upload file
                      </Button>
                    </Upload>
                  </Space>
                  <Table<MocAttachmentRow & { _key: number }>
                    rowKey="_key"
                    size="small"
                    pagination={false}
                    dataSource={attachments.map((r, i) => ({ ...r, _key: i }))}
                    columns={[
                      {
                        title: 'Kind',
                        width: 140,
                        render: (_, __, index) => attachments[index]?.attachment_kind ?? '—',
                      },
                      {
                        title: 'Title',
                        render: (_, __, index) => attachments[index]?.title ?? '—',
                      },
                      {
                        title: 'File',
                        width: 120,
                        render: (_, __, index) => {
                          const docId = attachments[index]?.document_id;
                          if (!docId) return '—';
                          return (
                            <Button
                              type="link"
                              icon={<FileOutlined />}
                              onClick={() => void downloadMocDocument(docId, String(attachments[index]?.title || 'file'))}
                            >
                              Download
                            </Button>
                          );
                        },
                      },
                      {
                        title: '',
                        width: 90,
                        render: (_, __, index) => (
                          <Button type="link" danger disabled={blockedEdit} onClick={() => void removeAttachmentAt(index)}>
                            Remove
                          </Button>
                        ),
                      },
                    ]}
                  />
                </Space>
              ),
            },
            {
              key: 'history',
              label: 'Status history',
              children: (
                <Table
                  rowKey="id"
                  size="small"
                  pagination={false}
                  dataSource={histSorted}
                  columns={[
                    { title: 'When', dataIndex: 'created_at', render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm') },
                    { title: 'From', dataIndex: 'from_status', render: (v: string | null) => v?.replace(/_/g, ' ') ?? '—' },
                    { title: 'To', dataIndex: 'to_status', render: (v: string) => v.replace(/_/g, ' ') },
                    { title: 'Remarks', dataIndex: 'remarks' },
                  ]}
                />
              ),
            },
          ]}
        />
      </Space>
    </div>
  );
};

export default MOCDetailPage;
